import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import FileUploader from "@/components/admin/FileUploader";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Eye, EyeOff, Home as HomeIcon, Image as ImageIcon } from "lucide-react";

const SECTION_LABELS = {
  upcoming: "Upcoming Meetings",
  notices: "Notices & Alerts",
  minutes: "Recent Meeting Minutes",
  quick: "Quick Links",
};

const SortableSection = ({ section, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 bg-white border border-border rounded-md p-4"
      data-testid={`home-section-${section.key}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        aria-label="Drag to reorder"
        data-testid={`home-section-handle-${section.key}`}
      >
        <GripVertical size={18} />
      </button>
      <div className="flex-1">
        <div className="font-medium">{SECTION_LABELS[section.key] || section.key}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {section.visible ? "Visible on home page" : "Hidden — won’t show on the public site"}
        </div>
      </div>
      <Button
        type="button"
        variant={section.visible ? "outline" : "default"}
        size="sm"
        onClick={() => onToggle(section.key)}
        data-testid={`home-section-toggle-${section.key}`}
      >
        {section.visible ? (
          <><EyeOff size={14} className="mr-1.5" /> Hide</>
        ) : (
          <><Eye size={14} className="mr-1.5" /> Show</>
        )}
      </Button>
    </div>
  );
};

const AdminHome = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  if (!settings) return <div className="text-muted-foreground">Loading…</div>;

  const update = (patch) => setSettings({ ...settings, ...patch });

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = settings.home_sections.findIndex((s) => s.key === active.id);
    const newIdx = settings.home_sections.findIndex((s) => s.key === over.id);
    update({ home_sections: arrayMove(settings.home_sections, oldIdx, newIdx) });
  };

  const toggle = (key) => {
    update({
      home_sections: settings.home_sections.map((s) =>
        s.key === key ? { ...s, visible: !s.visible } : s,
      ),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hero_file_id: settings.hero_file_id,
        hero_title: settings.hero_title,
        hero_subtitle: settings.hero_subtitle,
        home_sections: settings.home_sections,
      };
      const res = await api.put("/settings", payload);
      setSettings(res.data);
      toast.success("Home page updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-home-page" className="max-w-4xl">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <HomeIcon size={22} className="text-accent" /> Home Page
          </h1>
          <p className="text-muted-foreground mt-1">
            Edit the hero photo and headline, then drag the sections below to reorder or hide them.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="admin-home-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <Card className="border-border mb-6" data-testid="admin-home-hero-card">
        <CardContent className="p-7">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon size={16} className="text-accent" />
            <h2 className="font-heading text-lg font-semibold">Hero photo & headline</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Hero photo</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">
                Replaces the lake photo at the very top of the home page. Leave empty to use the default.
              </p>
              <FileUploader
                kind="image"
                value={settings.hero_file_id || ""}
                onChange={(id) => update({ hero_file_id: id })}
                testKey="admin-home-hero-upload"
              />
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="hero-title">Headline</Label>
                <Input
                  id="hero-title"
                  value={settings.hero_title || ""}
                  onChange={(e) => update({ hero_title: e.target.value })}
                  className="mt-1.5"
                  data-testid="admin-home-hero-title"
                />
              </div>
              <div>
                <Label htmlFor="hero-sub">Sub-headline</Label>
                <Textarea
                  id="hero-sub"
                  rows={5}
                  value={settings.hero_subtitle || ""}
                  onChange={(e) => update({ hero_subtitle: e.target.value })}
                  className="mt-1.5"
                  data-testid="admin-home-hero-subtitle"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-7">
          <h2 className="font-heading text-lg font-semibold mb-1">Home page sections</h2>
          <p className="text-sm text-muted-foreground mb-5">
            Drag a section to reorder it. Click <em>Hide</em> to remove it from the public home page.
          </p>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={settings.home_sections.map((s) => s.key)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3" data-testid="admin-home-sections-list">
                {settings.home_sections.map((s) => (
                  <SortableSection key={s.key} section={s} onToggle={toggle} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </CardContent>
      </Card>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="admin-home-save-btn-bottom">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

export default AdminHome;
